import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Segment, Header, Message, Tab, Card, Statistic, Table, Label, Dropdown, Button, Icon, Grid, Progress } from 'semantic-ui-react';
import GlassPageWrapper from '../../../components/Layout/GlassPageWrapper';
import { useAuth } from '../../../context/AuthContext';
import { useToast } from '../../../hooks/useToast';
import { quizReportService, TrainingMetricsDto, ParticipantComparisonDto, TeamComparisonDto, TopPerformersDto, TemporalAnalysisDto } from '../../../services/learning/QuizReportService';
import { teamService, TeamListDto } from '../../../services/teams/teamService';

const QuizReportsPage: React.FC = () => {
  const { hasRole } = useAuth();
  const navigate = useNavigate();
  const { showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [trainingMetrics, setTrainingMetrics] = useState<TrainingMetricsDto | null>(null);
  const [participantsComparison, setParticipantsComparison] = useState<ParticipantComparisonDto[]>([]);
  const [teamsComparison, setTeamsComparison] = useState<TeamComparisonDto[]>([]);
  const [topPerformers, setTopPerformers] = useState<TopPerformersDto[]>([]);
  const [temporalAnalysis, setTemporalAnalysis] = useState<TemporalAnalysisDto | null>(null);
  const [trainingTeams, setTrainingTeams] = useState<TeamListDto[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (!hasRole('Admin') && !hasRole('Supervisor')) {
      return;
    }

    loadData();
    loadTrainingTeams();
  }, [hasRole, selectedTeamId]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const [metrics, participants, teams, performers] = await Promise.all([
        quizReportService.getTrainingMetrics(),
        quizReportService.getParticipantsComparison(selectedTeamId),
        quizReportService.getTeamsComparison(),
        quizReportService.getTopPerformers(selectedTeamId, undefined, undefined, undefined, 10)
      ]);

      setTrainingMetrics(metrics);
      setParticipantsComparison(participants);
      setTeamsComparison(teams);
      setTopPerformers(performers);

      // Carregar análise temporal (últimos 30 dias)
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      
      const temporal = await quizReportService.getTemporalAnalysis(
        startDate.toISOString(),
        endDate.toISOString()
      );
      setTemporalAnalysis(temporal);
    } catch (err: any) {
      showError(err.message || 'Erro ao carregar relatórios');
    } finally {
      setLoading(false);
    }
  };

  const loadTrainingTeams = async () => {
    try {
      const teams = await teamService.getTrainingTeams();
      setTrainingTeams(teams);
    } catch (err: any) {
      console.error('Erro ao carregar times de treinamento:', err);
    }
  };

  if (!hasRole('Admin') && !hasRole('Supervisor')) {
    return (
      <GlassPageWrapper title="Acesso Negado" icon="lock">
        <Message negative>
          <Message.Header>Acesso Restrito</Message.Header>
          <p>Você não tem permissão para acessar esta página.</p>
        </Message>
      </GlassPageWrapper>
    );
  }

  const panes = [
    {
      menuItem: 'Visão Geral',
      render: () => (
        <Tab.Pane loading={loading}>
          {trainingMetrics && (
            <Grid columns={4} stackable>
              <Grid.Column>
                <Card>
                  <Card.Content>
                    <Statistic>
                      <Statistic.Value>{trainingMetrics.totalTrainingTeams}</Statistic.Value>
                      <Statistic.Label>Times de Treinamento</Statistic.Label>
                    </Statistic>
                  </Card.Content>
                </Card>
              </Grid.Column>
              <Grid.Column>
                <Card>
                  <Card.Content>
                    <Statistic>
                      <Statistic.Value>{trainingMetrics.totalTrainees}</Statistic.Value>
                      <Statistic.Label>Total de Trainees</Statistic.Label>
                    </Statistic>
                  </Card.Content>
                </Card>
              </Grid.Column>
              <Grid.Column>
                <Card>
                  <Card.Content>
                    <Statistic color="green">
                      <Statistic.Value>{trainingMetrics.overallApprovalRate.toFixed(1)}%</Statistic.Value>
                      <Statistic.Label>Taxa de Aprovação</Statistic.Label>
                    </Statistic>
                  </Card.Content>
                </Card>
              </Grid.Column>
              <Grid.Column>
                <Card>
                  <Card.Content>
                    <Statistic>
                      <Statistic.Value>{trainingMetrics.overallAverageScore.toFixed(1)}%</Statistic.Value>
                      <Statistic.Label>Média Geral</Statistic.Label>
                    </Statistic>
                  </Card.Content>
                </Card>
              </Grid.Column>
            </Grid>
          )}

          {topPerformers.length > 0 && (
            <Segment style={{ marginTop: '20px' }}>
              <Header as="h3">
                <Icon name="trophy" />
                Top 10 Performers
              </Header>
              <Table celled>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell>Rank</Table.HeaderCell>
                    <Table.HeaderCell>Nome</Table.HeaderCell>
                    <Table.HeaderCell>Time</Table.HeaderCell>
                    <Table.HeaderCell>Média</Table.HeaderCell>
                    <Table.HeaderCell>Melhor Nota</Table.HeaderCell>
                    <Table.HeaderCell>Questionários Aprovados</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {topPerformers.map((performer) => (
                    <Table.Row key={performer.userId}>
                      <Table.Cell>
                        <Label color={performer.rank <= 3 ? 'yellow' : undefined}>
                          {performer.rank}º
                        </Label>
                      </Table.Cell>
                      <Table.Cell>{performer.userName}</Table.Cell>
                      <Table.Cell>{performer.teamName || 'N/A'}</Table.Cell>
                      <Table.Cell>{performer.averageScore.toFixed(1)}%</Table.Cell>
                      <Table.Cell>{performer.bestScore?.toFixed(1) || 'N/A'}%</Table.Cell>
                      <Table.Cell>{performer.totalQuizzesPassed}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </Segment>
          )}
        </Tab.Pane>
      )
    },
    {
      menuItem: 'Comparação de Participantes',
      render: () => (
        <Tab.Pane loading={loading}>
          <div style={{ marginBottom: '20px' }}>
            <Dropdown
              placeholder="Filtrar por time"
              clearable
              selection
              options={trainingTeams.map(team => ({
                key: team.id,
                text: team.name,
                value: team.id
              }))}
              value={selectedTeamId}
              onChange={(e, { value }) => setSelectedTeamId(value as string | undefined)}
            />
            <Button
              icon="refresh"
              content="Atualizar"
              onClick={loadData}
              style={{ marginLeft: '10px' }}
            />
          </div>

          {participantsComparison.length > 0 ? (
            <Table celled sortable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Rank</Table.HeaderCell>
                  <Table.HeaderCell>Nome</Table.HeaderCell>
                  <Table.HeaderCell>Turma</Table.HeaderCell>
                  <Table.HeaderCell>Média</Table.HeaderCell>
                  <Table.HeaderCell>Taxa de Aprovação</Table.HeaderCell>
                  <Table.HeaderCell>Aprovados</Table.HeaderCell>
                  <Table.HeaderCell>Reprovados</Table.HeaderCell>
                  <Table.HeaderCell>Total de Tentativas</Table.HeaderCell>
                  <Table.HeaderCell>Ações</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {participantsComparison.map((participant) => (
                  <Table.Row key={participant.userId}>
                    <Table.Cell>
                      <Label color={participant.rank <= 3 ? 'yellow' : undefined}>
                        {participant.rank}º
                      </Label>
                    </Table.Cell>
                    <Table.Cell>{participant.userName}</Table.Cell>
                    <Table.Cell>{participant.teamName || 'N/A'}</Table.Cell>
                    <Table.Cell>{participant.averageScore?.toFixed(1) || 'N/A'}%</Table.Cell>
                    <Table.Cell>
                      <Progress
                        percent={participant.approvalRate}
                        color={participant.approvalRate >= 70 ? 'green' : participant.approvalRate >= 50 ? 'yellow' : 'red'}
                        size="small"
                      >
                        {participant.approvalRate.toFixed(1)}%
                      </Progress>
                    </Table.Cell>
                    <Table.Cell>
                      <Label color="green">{participant.passedAttempts}</Label>
                    </Table.Cell>
                    <Table.Cell>
                      <Label color="red">{participant.failedAttempts}</Label>
                    </Table.Cell>
                    <Table.Cell>{participant.totalAttempts}</Table.Cell>
                    <Table.Cell>
                      <Button
                        size="small"
                        icon="history"
                        content="Ver tentativas"
                        onClick={() => navigate(`/learning/quizzes/participants/${participant.userId}/attempts`)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <Message info>
              <Message.Header>Nenhum dado disponível</Message.Header>
              <p>Não há dados de participantes para exibir no momento.</p>
            </Message>
          )}
        </Tab.Pane>
      )
    },
    {
      menuItem: 'Comparação de Times',
      render: () => (
        <Tab.Pane loading={loading}>
          {teamsComparison.length > 0 ? (
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Turma</Table.HeaderCell>
                  <Table.HeaderCell>Trainees</Table.HeaderCell>
                  <Table.HeaderCell>Aprovados</Table.HeaderCell>
                  <Table.HeaderCell>Reprovados</Table.HeaderCell>
                  <Table.HeaderCell>Taxa de Aprovação</Table.HeaderCell>
                  <Table.HeaderCell>Média</Table.HeaderCell>
                  <Table.HeaderCell>Mediana</Table.HeaderCell>
                  <Table.HeaderCell>Desvio Padrão</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {teamsComparison.map((team) => (
                  <Table.Row key={team.teamId}>
                    <Table.Cell>
                      <strong>{team.teamName}</strong>
                      <div style={{ fontSize: '0.9em', color: '#666' }}>
                        {new Date(team.startDate).toLocaleDateString('pt-BR')} - 
                        {team.endDate ? new Date(team.endDate).toLocaleDateString('pt-BR') : 'Em andamento'}
                      </div>
                    </Table.Cell>
                    <Table.Cell>{team.totalTrainees}</Table.Cell>
                    <Table.Cell>
                      <Label color="green">{team.traineesPassed}</Label>
                    </Table.Cell>
                    <Table.Cell>
                      <Label color="red">{team.traineesFailed}</Label>
                    </Table.Cell>
                    <Table.Cell>
                      <Progress
                        percent={team.approvalRate}
                        color={team.approvalRate >= 70 ? 'green' : team.approvalRate >= 50 ? 'yellow' : 'red'}
                        size="small"
                      >
                        {team.approvalRate.toFixed(1)}%
                      </Progress>
                    </Table.Cell>
                    <Table.Cell>{team.averageScore.toFixed(1)}%</Table.Cell>
                    <Table.Cell>{team.medianScore?.toFixed(1) || 'N/A'}%</Table.Cell>
                    <Table.Cell>{team.standardDeviation?.toFixed(2) || 'N/A'}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          ) : (
            <Message info>
              <Message.Header>Nenhum dado disponível</Message.Header>
              <p>Não há dados de turmas para exibir no momento.</p>
            </Message>
          )}
        </Tab.Pane>
      )
    },
    {
      menuItem: 'Top Performers',
      render: () => (
        <Tab.Pane loading={loading}>
          {topPerformers.length > 0 ? (
            <>
              <Grid columns={3} stackable>
                {topPerformers.slice(0, 3).map((performer, index) => (
                  <Grid.Column key={performer.userId}>
                    <Card color={index === 0 ? 'yellow' : index === 1 ? 'grey' : index === 2 ? 'brown' : undefined}>
                      <Card.Content textAlign="center">
                        <Label size="large" color={index === 0 ? 'yellow' : undefined} style={{ marginBottom: '10px' }}>
                          {performer.rank}º Lugar
                        </Label>
                        <Header as="h3">{performer.userName}</Header>
                        <Statistic size="small">
                          <Statistic.Value>{performer.averageScore.toFixed(1)}%</Statistic.Value>
                          <Statistic.Label>Média</Statistic.Label>
                        </Statistic>
                        <div style={{ marginTop: '10px' }}>
                          <Label>{performer.teamName || 'N/A'}</Label>
                        </div>
                        <div style={{ marginTop: '10px', fontSize: '0.9em', color: '#666' }}>
                          {performer.totalQuizzesPassed} questionários aprovados
                        </div>
                      </Card.Content>
                    </Card>
                  </Grid.Column>
                ))}
              </Grid>

              <Segment style={{ marginTop: '20px' }}>
                <Header as="h3">Ranking Completo</Header>
                <Table celled>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Rank</Table.HeaderCell>
                      <Table.HeaderCell>Nome</Table.HeaderCell>
                      <Table.HeaderCell>Time</Table.HeaderCell>
                      <Table.HeaderCell>Média</Table.HeaderCell>
                      <Table.HeaderCell>Melhor Nota</Table.HeaderCell>
                      <Table.HeaderCell>Questionários Aprovados</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {topPerformers.map((performer) => (
                      <Table.Row key={performer.userId}>
                        <Table.Cell>
                          <Label color={performer.rank <= 3 ? 'yellow' : undefined}>
                            {performer.rank}º
                          </Label>
                        </Table.Cell>
                        <Table.Cell>{performer.userName}</Table.Cell>
                        <Table.Cell>{performer.teamName || 'N/A'}</Table.Cell>
                        <Table.Cell>{performer.averageScore.toFixed(1)}%</Table.Cell>
                        <Table.Cell>{performer.bestScore?.toFixed(1) || 'N/A'}%</Table.Cell>
                        <Table.Cell>{performer.totalQuizzesPassed}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table>
              </Segment>
            </>
          ) : (
            <Message info>
              <Message.Header>Nenhum dado disponível</Message.Header>
              <p>Não há dados de top performers para exibir no momento.</p>
            </Message>
          )}
        </Tab.Pane>
      )
    }
  ];

  return (
    <GlassPageWrapper title="Relatórios e Análises" icon="chart bar">
      <Segment>
        <Header as="h3">Relatórios de Treinamento</Header>
        <Tab panes={panes} />
      </Segment>
    </GlassPageWrapper>
  );
};

export default QuizReportsPage;
